import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { range } from '@firsttable/functions';
import isEmpty from 'lodash/isEmpty';
import { Restaurant } from '@firsttable/types';
import { BoxProps } from '@firsttable/web-components/atoms/Box/Box';
import dayjs from 'dayjs';
import { allAvailabilityToAvailability } from '@/lib/functions';
import useAvailabilityDaysConfig from '@/components/hooks/useAvailabilityDaysConfig';

const withFireBaseAvailability = (WrappedComponent) => {
  const WithFireBaseAvailability = ({
    restaurant,
    allowFiltering = true,
    ...props
  }: {
    restaurant: Restaurant;
    availabilitySession: string; // the global availability session to show. this will be passed in at the restaurant level via search or filtering
    showAllAvailability?: boolean;
    selectedDate: string;
    allowFiltering: boolean;
    searchFilterState?: any; // todo type for city search filtering
    restaurantAvailabilityRef?: any; // todo type for city search date filtering
    restaurantSort?: boolean;
    NavComponent?: React.ReactNode;
    showFullAddress?: boolean;
  } & BoxProps) => {
    const [loading, setLoading] = useState(true);
    const [availability, setAvailabilityState] = useState({});
    const [currentSession, setCurrentSessionState] = useState(props.availabilitySession); // represents an internal currentSession state. Useful for modifying an individual restaurants session state e.g. dinner2
    const [forceDinnerSessionChange, setForceDinnerSessionChange] = useState(false);
    const [shouldShowRestaurant, setShouldShowRestaurant] = useState(true);
    const availabilityData = useRef(null);
    const restaurantAvailability = useRef(null);
    const { baseDaysToShow } = useAvailabilityDaysConfig(restaurant.bookingWindowDays);

    let unsubscribe;

    const setAvailability = async (requestedSession) => {
      let newSession = requestedSession;
      const { showAllAvailability, selectedDate } = props;

      const { getAppInstance, getDatabase, onValue, ref } = await import('@/lib/firebase');
      const app = getAppInstance();
      availabilityData.current = getDatabase(app);

      let showRestaurant = true;

      const { id } = restaurant;
      const availabilityRef = ref(availabilityData.current, `allAvailability/${id}`);

      unsubscribe = onValue(availabilityRef, (snapshot) => {
        // Converts old format to multiple time slot format.
        const baseSnapShot = allAvailabilityToAvailability(snapshot.val() ?? {});

        let availabilitySnapShot: any = baseSnapShot;

        if (!showAllAvailability) {
          availabilitySnapShot = availabilitySnapShot[newSession];
        }

        // only display availability for live restaurants, that have the currentAvailabilitySession;
        if (
          availabilitySnapShot &&
          ['Live', 'Ready To Go Live', 'Pending Approval', 'Pending'].includes(restaurant.status) &&
          restaurant.sessionTypes.includes(newSession)
        ) {
          // delete any sessions that don't exist in the restaurants available sessions
          if (showAllAvailability) {
            Object.keys(availabilitySnapShot).forEach((session) => {
              if (!restaurant.sessionTypes.includes(session)) {
                delete availabilitySnapShot[session];
              }
            });
          }

          // date filtering of restaurants for lunch and breakfast
          if (
            selectedDate !== 'any' &&
            ['lunch', 'breakfast'].includes(newSession) &&
            allowFiltering // don't show if all restaurant availability is requested ie restaurant page
          ) {
            const generalAvailability = baseSnapShot[currentSession][selectedDate];
            // if general availability
            showRestaurant = !!(generalAvailability && generalAvailability.available);
          }

          // date filtering of restaurants for dinner and dinner2

          if (
            selectedDate !== 'any' &&
            ['dinner', 'dinner2'].includes(newSession) &&
            allowFiltering // don't show if all restaurant availability is requested ie restaurant page
          ) {
            const dinner2Availability = baseSnapShot?.dinner2?.[`${selectedDate}`]
              ? baseSnapShot.dinner2[`${selectedDate}`]
              : null;
            const dinnerAvailability = baseSnapShot?.dinner?.[`${selectedDate}`]
              ? baseSnapShot.dinner[`${selectedDate}`]
              : null;

            // if current session is dinner or dinner and no availability, hide restaurant
            if (!dinnerAvailability?.available && !dinner2Availability?.available) {
              showRestaurant = false;
            }
            // if current session is dinner or dinner 2 and we have dinner 2 availability, default to dinner
            if (dinnerAvailability?.available && !forceDinnerSessionChange) {
              showRestaurant = true;
              newSession = 'dinner';
            }
            if (
              !dinnerAvailability?.available &&
              dinner2Availability?.available &&
              !forceDinnerSessionChange
            ) {
              showRestaurant = true;
              newSession = 'dinner2';
            }
          }

          setAvailabilityState(availabilitySnapShot || {});
          setLoading(false);
          setShouldShowRestaurant(showRestaurant);
          setCurrentSessionState(newSession);
          return;
        }

        // if there is date filtering and there is no availability for the restaurant, hide the restaurant
        if (
          ((!availabilitySnapShot &&
            ['Live', 'Ready To Go Live', 'Pending Approval', 'Pending'].includes(
              restaurant.status
            )) ||
            // always hide on hold venues when date filtering
            ['On Hold'].includes(restaurant.status)) &&
          selectedDate !== 'any' &&
          typeof selectedDate !== 'undefined' // selectedDate will be undefined on restaurant page
        ) {
          setShouldShowRestaurant(false);
          return;
        }

        // Mock the full booking window as closed availability when no data is available. this will run for any restaurant that isn't 'Live', has no firebase availability (hopefully this doesnt have) or doesn't have a session for the currently active one in search this can be the case in development or if it's a new restaurant and hasn't had availability loaded
        availabilitySnapShot = {};
        range(0, baseDaysToShow).forEach((i) => {
          const today = dayjs();
          const date = today.add(i, 'days').format('YYYY-MM-DD');
          availabilitySnapShot[date] = {
            id: uuidv4(),
            date,
            open: true,
            availability: false,
            session: currentSession,
          };
        });

        if (showAllAvailability) {
          // mock the availability for the requested session (if restaurant layout). This is use for restaurants with no availability in firebase or 'On Hold'
          const sessionAvailability = {};
          restaurant.sessionTypes.map(
            (mockSession) =>
              (sessionAvailability[mockSession] = {
                ...availabilitySnapShot,
              })
          );

          setAvailabilityState(sessionAvailability);
          setLoading(false);
          return;
        }

        // @BR This only seems to be used when there is no availability and it should really be marked as on hold
        setAvailabilityState(availabilitySnapShot);
        setLoading(false);
      });
    };

    // callback for items to mutate the availability session. Mainly used for Last Table/Dinner session changing
    const setCurrentSession = useCallback(() => {
      const newSession = currentSession === 'dinner' ? 'dinner2' : 'dinner';
      setCurrentSessionState(newSession);
      setForceDinnerSessionChange(true);
    }, [currentSession]);

    const load = typeof window !== 'undefined';

    useEffect(() => {
      let assignAvailability = true;
      if (load) {
        setLoading(true);

        // check this isn't unmounted
        if (assignAvailability) {
          setAvailability(currentSession);
        }
      }

      return function cleanUpFirebaseConnections() {
        assignAvailability = false;
        setAvailabilityState({});
        setLoading(true);
        if (availabilityData.current) {
          unsubscribe();
        }
        if (restaurantAvailability.current) {
          unsubscribe();
        }
      };
    }, [load]);

    /**
     * mimics componentDidUpdate
     * updates availability when session state is modified
     */
    useEffect(() => {
      if (availabilityData.current) {
        const callAvailability = async () => {
          await setAvailability(currentSession);
        };

        callAvailability();
      }
    }, [currentSession, props.selectedDate, availabilityData.current, restaurant.id]);

    useEffect(() => {
      const callAvailability = async () => {
        await setAvailability(props.availabilitySession);
      };

      callAvailability();
    }, [props.availabilitySession]);

    // pushes availability into the ref passed from city search
    useEffect(() => {
      if (
        props.selectedDate !== 'any' &&
        props.restaurantAvailabilityRef &&
        !isEmpty(availability)
      ) {
        const { id } = restaurant;
        const update = {};
        update[id] = availability;
        props.restaurantAvailabilityRef(update);
      }
    }, [props.selectedDate, availability]);

    if (allowFiltering && !shouldShowRestaurant) {
      return null;
    }

    return (
      <WrappedComponent
        isLoading={loading}
        availability={availability}
        setCurrentSession={setCurrentSession}
        currentSession={currentSession}
        restaurant={restaurant}
        {...props}
      />
    );
  };
  WithFireBaseAvailability.displayName = 'withFireBaseAvailability';
  return WithFireBaseAvailability;
};

export default withFireBaseAvailability;
